import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { GetPlayerListTableData } from '@/modules/manage-player/type'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import useMutationUpdatePlayer from '@/modules/manage-player/api/hooks/useMutationUpdatePlayer'
import { useQueryClient } from '@tanstack/react-query'
import { PlayerQueryKeys } from '@/modules/manage-player/keys'
import { Pencil } from 'lucide-react'
import { handlePlayerFormError } from '@/modules/manage-player/utils/handlePlayerFormError'
import { BankSelector } from '@/modules/bank/components/BankSelector'
import { RequiredIndicator } from '@/common/ui/form/required-indicator'

const formSchema = z.object({
	// presetId: z.string().optional().nullable(),
	bankProvider: z.string(),
	accountName: z.string().min(1, 'กรุณากรอกชื่อบัญชี'),
	accountNumber: z.string().min(1, 'กรุณากรอกเลขบัญชี'),
})

type FormValues = z.infer<typeof formSchema>

interface PlayerEditFormModalProps {
	player: GetPlayerListTableData
}

export const PlayerEditFormModal = ({ player }: PlayerEditFormModalProps) => {
	const [open, setOpen] = useState(false)

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			// presetId: player?.presetId,
			bankProvider: player?.bank?.bankProvider,
			accountName: player?.bank?.accountName || '',
			accountNumber: player?.bank?.accountNumber || '',
		},
	})

	useEffect(() => {
		if (open) {
			form.reset({
				// presetId: player?.presetId,
				bankProvider: player?.bank?.bankProvider,
				accountName: player?.bank?.accountName || '',
				accountNumber: player?.bank?.accountNumber || '',
			})
		}
	}, [open])

	const { mutate: updatePlayer } = useMutationUpdatePlayer()
	const queryClient = useQueryClient()
	const onSubmit = (values: FormValues) => {
		try {
			updatePlayer(
				{
					id: player.id,
					payload: {
						...values,
						// presetId: values.presetId || undefined,
					},
				},
				{
					onSuccess: () => {
						toast.success('แก้ไขข้อมูลเรียบร้อยแล้ว')
						queryClient.invalidateQueries({
							queryKey: [PlayerQueryKeys.GetPlayerList],
						})
						form.reset()
						setOpen(false)
					},
					onError: (error) => {
						handlePlayerFormError(error, form, 'edit')
					},
				},
			)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	return (
		<>
			<Button variant="ghost" size="icon" onClick={() => setOpen(true)}>
				<Pencil className="h-4 w-4" />
			</Button>
			{open && (
				<Dialog open onOpenChange={setOpen}>
					<DialogContent className="sm:max-w-[600px]">
						<DialogHeader>
							<DialogTitle>
								แก้ไขข้อมูลสมาชิกเว็บ ({player.username || player.phone})
							</DialogTitle>
						</DialogHeader>

						<Form {...form}>
							<div className="space-y-4">
								<form
									onSubmit={form.handleSubmit(onSubmit)}
									className="max-h-[70vh] space-y-4 overflow-y-auto pr-2"
								>
									{/* <FormField
										control={form.control}
										name="presetId"
										render={({ field }) => (
											<FormItem>
												<FormLabel>preset</FormLabel>
												<FormControl>
													<Input {...field} value={field.value || ''} />
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/> */}

									<BankSelector control={form.control} label="ธนาคาร" />

									<FormField
										control={form.control}
										name="accountName"
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													<RequiredIndicator /> ชื่อบัญชี
												</FormLabel>
												<FormControl>
													<Input {...field} />
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name="accountNumber"
										render={({ field }) => {
											const selectedBank = form.watch('bankProvider')
											const isTrueMoney = selectedBank === 'TrueMoney'
											const label = isTrueMoney ? 'เบอร์โทรศัพท์' : 'เลขบัญชี'
											const placeholder = isTrueMoney
												? 'กรอกเบอร์โทรศัพท์'
												: 'กรอกเลขบัญชี'

											return (
												<FormItem>
													<FormLabel>
														<RequiredIndicator /> {label}
													</FormLabel>
													<FormControl>
														<Input {...field} placeholder={placeholder} />
													</FormControl>
													<FormMessage />
												</FormItem>
											)
										}}
									/>

									<div className="flex justify-end gap-4 pt-4">
										<Button
											variant="outline"
											onClick={() => setOpen(false)}
											type="button"
										>
											ยกเลิก
										</Button>
										<Button type="submit" variant="default">
											บันทึก
										</Button>
									</div>
								</form>
							</div>
						</Form>
					</DialogContent>
				</Dialog>
			)}
		</>
	)
}
